import { __read, __spreadArray } from "tslib";
/**
 * @see https://github.com/NervJS/taro/blob/next/packages/taro-components/src/components/scroll-view/scroll-view.tsx
 */
import React, { useEffect, useLayoutEffect, useRef } from "react";
import classNames from "classnames";
import { noop } from "../../../_util/noop";
import { mergeRefs } from "../../../_util/merge-refs";
import { createEvent } from "../_util/create-event";
function debounce(fn, delay) {
    var timer;
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        clearTimeout(timer);
        timer = setTimeout(function () {
            fn.apply(void 0, __spreadArray([], __read(args)));
        }, delay);
    };
}
function easeOutScroll(from, to, callback) {
    if (from === to || typeof from !== "number") {
        return;
    }
    var change = to - from;
    var dur = 500;
    var sTime = Date.now();
    var isLarger = to >= from;
    function linear(t, b, c, d) {
        return (c * t) / d + b;
    }
    function step() {
        // eslint-disable-next-line no-param-reassign
        from = linear(Date.now() - sTime, from, change, dur);
        if ((isLarger && from >= to) || (!isLarger && to >= from)) {
            callback(to);
            return;
        }
        callback(from);
        requestAnimationFrame(step);
    }
    step();
}
export var ScrollView = React.forwardRef(function ScrollView(_a, ref) {
    var scrollX = _a.scrollX, scrollY = _a.scrollY, _b = _a.upperThreshold, upperThreshold = _b === void 0 ? 50 : _b, _c = _a.lowerThreshold, lowerThreshold = _c === void 0 ? 50 : _c, scrollTop = _a.scrollTop, scrollLeft = _a.scrollLeft, scrollIntoView = _a.scrollIntoView, scrollWithAnimation = _a.scrollWithAnimation, _d = _a.onScrollToUpper, onScrollToUpper = _d === void 0 ? noop : _d, _e = _a.onScrollToLower, onScrollToLower = _e === void 0 ? noop : _e, _f = _a.onScroll, onScroll = _f === void 0 ? noop : _f, className = _a.className, children = _a.children, style = _a.style;
    var viewRef = useRef(null);
    var scrollTopRef = useRef(0);
    var scrollLeftRef = useRef(0);
    useEffect(function () {
        var top = Number(scrollTop);
        var left = Number(scrollLeft);
        if (scrollY && !Number.isNaN(scrollTop)) {
            if (scrollWithAnimation) {
                easeOutScroll(0, top, function (pos) {
                    viewRef.current.scrollTop = pos;
                });
            }
            else {
                viewRef.current.scrollTop = top;
            }
            scrollTopRef.current = top;
        }
        if (scrollX && !Number.isNaN(left)) {
            if (scrollWithAnimation) {
                easeOutScroll(0, left, function (pos) {
                    viewRef.current.scrollLeft = pos;
                });
            }
            else {
                viewRef.current.scrollLeft = left;
            }
            scrollLeftRef.current = left;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useLayoutEffect(function () {
        var top = Number(scrollTop);
        if (scrollY && !Number.isNaN(top) && top !== scrollTopRef.current) {
            if (scrollWithAnimation) {
                easeOutScroll(scrollTopRef.current, top, function (pos) {
                    viewRef.current.scrollTop = pos;
                });
            }
            else {
                viewRef.current.scrollTop = top;
            }
            scrollTopRef.current = top;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollTop]);
    useLayoutEffect(function () {
        var left = Number(scrollLeft);
        if (scrollX && !Number.isNaN(left) && left !== scrollLeftRef.current) {
            if (scrollWithAnimation) {
                easeOutScroll(scrollLeftRef.current, left, function (pos) {
                    viewRef.current.scrollLeft = pos;
                });
            }
            else {
                viewRef.current.scrollLeft = left;
            }
            scrollLeftRef.current = left;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollLeft]);
    useLayoutEffect(function () {
        var _a;
        if (typeof scrollIntoView === "string" && scrollIntoView) {
            (_a = document.querySelector("#" + scrollIntoView)) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "start",
            });
        }
    }, [scrollIntoView]);
    var upperAndLower = debounce(function (event) {
        var _a = viewRef.current, offsetWidth = _a.offsetWidth, offsetHeight = _a.offsetHeight, scrollLeft = _a.scrollLeft, scrollTop = _a.scrollTop, scrollHeight = _a.scrollHeight, scrollWidth = _a.scrollWidth;
        var lThreshold = Number(lowerThreshold);
        var uThreshold = Number(upperThreshold);
        if (!Number.isNaN(lThreshold) &&
            ((scrollY && offsetHeight + scrollTop + lThreshold >= scrollHeight) ||
                (scrollX && offsetWidth + scrollLeft + lThreshold >= scrollWidth))) {
            onScrollToLower(createEvent(event, {
                // eslint-disable-next-line no-nested-ternary
                direction: scrollX ? "right" : scrollY ? "bottom" : "",
            }));
        }
        if (!Number.isNaN(uThreshold) &&
            ((scrollY && scrollTop <= uThreshold) ||
                (scrollX && scrollLeft <= uThreshold))) {
            onScrollToUpper(createEvent(event, {
                // eslint-disable-next-line no-nested-ternary
                direction: scrollX ? "left" : scrollY ? "top" : "",
            }));
        }
    }, 200);
    function handleScroll(event) {
        if (!viewRef.current || event instanceof CustomEvent) {
            return;
        }
        var _a = viewRef.current, scrollLeft = _a.scrollLeft, scrollTop = _a.scrollTop, scrollHeight = _a.scrollHeight, scrollWidth = _a.scrollWidth;
        scrollLeftRef.current = scrollLeft;
        scrollTopRef.current = scrollTop;
        upperAndLower(event);
        onScroll(createEvent(event, {
            scrollLeft: scrollLeft,
            scrollTop: scrollTop,
            scrollHeight: scrollHeight,
            scrollWidth: scrollWidth,
        }));
    }
    return (React.createElement("div", { ref: mergeRefs(ref, viewRef), className: classNames("tea-core-scroll-view", className, {
            "tea-core-scroll-view__scroll-x": scrollX,
            "tea-core-scroll-view__scroll-y": scrollY,
        }), style: style, onScroll: handleScroll }, children));
});
//# sourceMappingURL=ScrollView.js.map